import { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, Plus, FileText, Link2, Upload, Trash2, ExternalLink, Lock, Eye } from "lucide-react";
import { format, parseISO } from "date-fns";

const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "hr", label: "HR" },
  { id: "policies", label: "Policies" },
  { id: "forms", label: "Forms" },
  { id: "onboarding", label: "Onboarding" },
  { id: "facilities", label: "Facilities" },
  { id: "finance", label: "Finance" },
  { id: "other", label: "Other" },
];

const VIS_LABEL = {
  all: { label: "Everyone", icon: Eye, color: "#AEBDB1" },
  pastoral: { label: "Pastoral+", icon: Lock, color: "#C69E5F" },
  admin_only: { label: "Admin only", icon: Lock, color: "#B2674A" },
};

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "hr", visibility: "all",
    link_url: "", file: null,
  });

  const load = async () => {
    const { data } = await api.get("/documents");
    setDocs(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return docs.filter((d) => {
      if (cat !== "all" && d.category !== cat) return false;
      if (q && !(`${d.title} ${d.description ?? ""}`.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [docs, cat, q]);

  const submit = async () => {
    if (!form.title || !form.category) {
      toast.error("Title and category required");
      return;
    }
    if (!form.link_url && !form.file) {
      toast.error("Provide a link or upload a file");
      return;
    }
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("description", form.description || "");
    fd.append("category", form.category);
    fd.append("visibility", form.visibility);
    if (form.link_url) fd.append("link_url", form.link_url);
    if (form.file) fd.append("file", form.file);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added");
      setOpen(false);
      setForm({ title: "", description: "", category: "hr", visibility: "all", link_url: "", file: null });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Unable to add document");
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Cannot remove this document");
    }
  };

  /**
   * Download a document via the authenticated axios client instead of a
   * native <a href="…" target="_blank"> link. Native links attach the
   * session cookie automatically on same-origin requests, but when the
   * frontend is authenticated via a Bearer token in localStorage (fallback
   * path for iOS PWA cookie-jar quirks) the browser never sends that
   * header on plain navigation clicks. The result was a 401 pop-up in the
   * new tab. This flow fetches the file as a blob (Authorization header
   * attached by the api axios interceptor), then triggers a synthetic
   * download so the file lands with the correct filename.
   */
  const downloadDoc = async (d) => {
    try {
      const resp = await api.get(`/documents/${d.id}/file`, { responseType: "blob" });
      const blob = new Blob([resp.data], {
        type: resp.headers["content-type"] || d.content_type || "application/octet-stream",
      });
      // Prefer the filename echoed by Content-Disposition; fall back to the
      // stored file_name so old records without CD still work.
      const cd = resp.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = (match && match[1]) || d.file_name || `${d.id}.bin`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke the object URL on next tick so the browser has time to start
      // the download before the blob is garbage-collected.
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Download failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="documents-hub">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-primary/50" />
            <Input
              data-testid="search-documents-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search documents…"
              className="pl-10 h-11 w-80 rounded-full bg-white border-brand-primary/10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`docs-cat-${c.id}`}
                className={`px-4 py-1.5 rounded-full text-sm transition-all duration-300 border-b-2 ${
                  cat === c.id
                    ? "border-terracotta text-terracotta font-medium"
                    : "border-transparent text-brand-primary/70 hover:text-brand-primary"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-document-button" className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white h-10 px-5 shadow-warm">
              <Plus className="h-4 w-4 mr-1.5" /> Add document
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Add document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <Input data-testid="doc-title-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <Textarea data-testid="doc-desc-input" placeholder="Description (optional)" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="doc-cat-select"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter((c) => c.id !== "all").map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                  <SelectTrigger data-testid="doc-vis-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Everyone</SelectItem>
                    <SelectItem value="pastoral">Pastoral + Admin</SelectItem>
                    <SelectItem value="admin_only">Admin only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input data-testid="doc-link-input" placeholder="Link URL (Google Drive, SharePoint, etc.)" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} />
              <div className="flex items-center gap-3">
                <label className="flex-1 cursor-pointer border border-dashed border-brand-primary/20 rounded-xl p-4 text-center text-sm text-brand-primary/70 hover:border-terracotta/50 transition">
                  <Upload className="h-5 w-5 mx-auto mb-1 text-terracotta" />
                  {form.file ? form.file.name : "Or upload a file"}
                  <input
                    type="file"
                    className="hidden"
                    data-testid="doc-file-input"
                    onChange={(e) => setForm({ ...form, file: e.target.files?.[0] ?? null })}
                  />
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="doc-submit-button" onClick={submit} className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="documents-grid">
        {filtered.length === 0 && (
          <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-8 text-brand-primary/60 col-span-full text-center">
            No documents match your search.
          </Card>
        )}
        {filtered.map((d) => {
          const V = VIS_LABEL[d.visibility] ?? VIS_LABEL.all;
          const VIcon = V.icon;
          return (
            <Card key={d.id} className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-warm-lg flex flex-col">
              <div className="flex items-start justify-between gap-3">
                <div className="h-11 w-11 rounded-xl bg-terracotta/10 grid place-items-center">
                  {d.file_name ? <FileText className="h-5 w-5 text-terracotta" strokeWidth={1.7} /> : <Link2 className="h-5 w-5 text-terracotta" strokeWidth={1.7} />}
                </div>
                <Badge className="rounded-full border-0" style={{ background: `${V.color}20`, color: V.color }}>
                  <VIcon className="h-3 w-3 mr-1" /> {V.label}
                </Badge>
              </div>
              <p className="font-heading text-lg font-medium text-brand-primary mt-4">{d.title}</p>
              {d.description && <p className="text-sm text-brand-primary/70 mt-1">{d.description}</p>}
              <div className="text-xs text-brand-primary/50 mt-3 flex items-center gap-2">
                <Badge variant="outline" className="rounded-full text-[10px] uppercase tracking-wider border-brand-primary/15">
                  {CATEGORIES.find((c) => c.id === d.category)?.label ?? d.category}
                </Badge>
                <span>· {d.uploader_name} · {format(parseISO(d.created_at), "MMM d")}</span>
              </div>
              <div className="flex items-center gap-2 mt-5 pt-5 border-t border-brand-primary/10">
                {d.file_name && (
                  <button
                    type="button"
                    onClick={() => downloadDoc(d)}
                    data-testid={`doc-download-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-sm text-terracotta hover:underline bg-transparent p-0"
                  >
                    Download <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                )}
                {d.link_url && (
                  <a
                    href={d.link_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`doc-link-${d.id}`}
                    className="inline-flex items-center gap-1.5 text-sm text-terracotta hover:underline"
                  >
                    Open link <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                {(user?.role === "admin" || user?.user_id === d.uploaded_by) && (
                  <button data-testid={`doc-delete-${d.id}`} onClick={() => del(d.id)} className="ml-auto text-brand-primary/40 hover:text-terracotta">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
